import React, { useEffect, useState } from "react";
import client, { apiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { rupiah, numberFmt, dateTimeFmt } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ClipboardCheck, SlidersHorizontal, History, AlertTriangle, Search } from "lucide-react";
import { usePaged, Paginator } from "@/components/Paginator";

export default function Stock() {
  const { user } = useAuth();
  const { effectiveBranch, isAllBranch } = useBranch();
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [adjust, setAdjust] = useState(null);
  const [adjQty, setAdjQty] = useState("");
  const [adjReason, setAdjReason] = useState("rusak");
  const [minFor, setMinFor] = useState(null);
  const [minVal, setMinVal] = useState("");
  const [opn, setOpn] = useState(null);
  const [physical, setPhysical] = useState("");
  const [card, setCard] = useState(null);
  const [cardRows, setCardRows] = useState([]);

  const branchParam = effectiveBranch ? { branch_id: effectiveBranch } : {};
  const load = () => client.get("/stock", { params: { ...branchParam, filter: filter === "all" ? undefined : filter } }).then((r) => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [effectiveBranch, filter]);
  useEffect(() => { client.get("/categories").then((r) => setCats(r.data)); }, []);

  const canWrite = ["super_admin", "admin", "branch_head", "warehouse"].includes(user.role) && effectiveBranch;
  const showCost = user.role !== "branch_head";
  const q = search.trim().toLowerCase();
  const filteredRows = rows.filter((p) =>
    (catFilter === "all" || p.category_id === catFilter) &&
    (!q || (p.name || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q) || (p.barcode || "").toLowerCase().includes(q))
  );
  const paged = usePaged(filteredRows, 10);
  const colSpan = showCost ? 7 : 5;

  const doAdjust = async () => {
    try {
      await client.post("/stock/adjust", { product_id: adjust.id, branch_id: effectiveBranch, qty_base: Number(adjQty), reason: adjReason });
      toast.success("Stok disesuaikan"); setAdjust(null); setAdjQty(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const doMin = async () => {
    try {
      await client.post("/stock/min", { product_id: minFor.id, branch_id: effectiveBranch, min_stock: Number(minVal) });
      toast.success("Minimum stok diperbarui"); setMinFor(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const doOpname = async () => {
    try {
      await client.post("/stock/opname", { branch_id: effectiveBranch, items: [{ product_id: opn.id, physical_qty: Number(physical), reason: "stock opname" }] });
      toast.success("Opname tersimpan"); setOpn(null); setPhysical(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const openCard = async (p) => {
    setCard(p);
    const r = await client.get("/stock/card", { params: { product_id: p.id, ...branchParam } });
    setCardRows(r.data);
  };

  const typeLabel = { purchase: "Pembelian", sale: "Penjualan", adjustment: "Penyesuaian", opname: "Opname", transfer_in: "Transfer Masuk", transfer_out: "Transfer Keluar" };

  return (
    <div className="p-6 space-y-5 animate-in-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Stok</h1>
          <p className="text-sm text-muted-foreground">Stok real-time, kartu stok, opname & penyesuaian</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => { setSearch(e.target.value); paged.setPage(1); }} placeholder="Cari produk..." className="w-56 pl-8" data-testid="stock-search" />
          </div>
          <Select value={catFilter} onValueChange={(v) => { setCatFilter(v); paged.setPage(1); }}>
            <SelectTrigger className="w-48" data-testid="stock-cat-filter"><SelectValue placeholder="Semua Kategori" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kategori</SelectItem>
              {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48" data-testid="stock-filter"><SlidersHorizontal className="mr-2 h-4 w-4" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              <SelectItem value="low">Stok Rendah</SelectItem>
              <SelectItem value="out">Stok Habis</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isAllBranch && !effectiveBranch && (
        <div className="flex items-center gap-2 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-700">
          <AlertTriangle className="h-4 w-4" /> Pilih cabang untuk melakukan opname/penyesuaian (saat ini menampilkan total semua cabang).
        </div>
      )}

      <div className="rounded-lg border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produk</TableHead>
              <TableHead>Satuan</TableHead>
              <TableHead className="text-right">Stok</TableHead>
              <TableHead className="text-right">Min</TableHead>
              {showCost && <TableHead className="text-right">HPP Rata2</TableHead>}
              {showCost && <TableHead className="text-right">Nilai Stok</TableHead>}
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.slice.map((p) => {
              const low = p.min_stock > 0 && p.stock_qty > 0 && p.stock_qty <= p.min_stock;
              const out = p.stock_qty <= 0;
              return (
                <TableRow key={p.id} className="hover:bg-slate-50" data-testid={`stock-row-${p.id}`}>
                  <TableCell className="font-medium">
                    {p.name}
                    {out && <Badge className="ml-2 bg-rose-100 text-rose-700">Habis</Badge>}
                    {low && <Badge className="ml-2 bg-amber-100 text-amber-700">Rendah</Badge>}
                  </TableCell>
                  <TableCell>{p.base_unit}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{numberFmt(p.stock_qty)}</TableCell>
                  <TableCell className="text-right font-mono text-muted-foreground">{numberFmt(p.min_stock)}</TableCell>
                  {showCost && <TableCell className="text-right font-mono text-muted-foreground">{rupiah(p.avg_cost)}</TableCell>}
                  {showCost && <TableCell className="text-right font-mono">{rupiah(p.avg_cost * p.stock_qty)}</TableCell>}
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openCard(p)} title="Kartu Stok" data-testid={`card-${p.id}`}><History className="h-4 w-4" /></Button>
                      {canWrite && <>
                        <Button size="icon" variant="ghost" onClick={() => { setMinFor(p); setMinVal(String(p.min_stock)); }} title="Set Minimum"><SlidersHorizontal className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => { setAdjust(p); setAdjQty(""); }} title="Penyesuaian" data-testid={`adjust-${p.id}`}><AlertTriangle className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => { setOpn(p); setPhysical(String(p.stock_qty)); }} title="Opname" data-testid={`opname-${p.id}`}><ClipboardCheck className="h-4 w-4" /></Button>
                      </>}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {!filteredRows.length && <TableRow><TableCell colSpan={colSpan} className="py-10 text-center text-muted-foreground">Tidak ada data</TableCell></TableRow>}
          </TableBody>
        </Table>
        <Paginator {...paged} />
      </div>

      {/* adjust */}
      <Dialog open={!!adjust} onOpenChange={(o) => !o && setAdjust(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Penyesuaian Stok</DialogTitle><DialogDescription>{adjust?.name}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Jumlah (negatif untuk keluar, mis. -2)</Label>
              <Input type="number" value={adjQty} onChange={(e) => setAdjQty(e.target.value)} className="font-mono" data-testid="adjust-qty" /></div>
            <div className="space-y-2"><Label>Alasan</Label>
              <Select value={adjReason} onValueChange={setAdjReason}>
                <SelectTrigger data-testid="adjust-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rusak">Barang Rusak</SelectItem>
                  <SelectItem value="hilang">Barang Hilang</SelectItem>
                  <SelectItem value="kadaluarsa">Kadaluarsa</SelectItem>
                  <SelectItem value="koreksi">Koreksi</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAdjust(null)}>Batal</Button><Button onClick={doAdjust} data-testid="save-adjust-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* min stock */}
      <Dialog open={!!minFor} onOpenChange={(o) => !o && setMinFor(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Minimum Stok</DialogTitle><DialogDescription>{minFor?.name}</DialogDescription></DialogHeader>
          <div className="space-y-2"><Label>Batas minimum (satuan dasar)</Label>
            <Input type="number" value={minVal} onChange={(e) => setMinVal(e.target.value)} className="font-mono" data-testid="min-val" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setMinFor(null)}>Batal</Button><Button onClick={doMin}>Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* opname */}
      <Dialog open={!!opn} onOpenChange={(o) => !o && setOpn(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Stock Opname</DialogTitle><DialogDescription>{opn?.name}</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-slate-100 px-3 py-2 text-sm">Stok sistem: <span className="font-mono font-semibold">{numberFmt(opn?.stock_qty)}</span></div>
            <div className="space-y-2"><Label>Jumlah fisik (hasil hitung)</Label>
              <Input type="number" value={physical} onChange={(e) => setPhysical(e.target.value)} className="font-mono" data-testid="opname-physical" /></div>
            {opn && <div className="text-sm">Selisih: <span className="font-mono font-semibold">{numberFmt((Number(physical) || 0) - opn.stock_qty)}</span></div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpn(null)}>Batal</Button><Button onClick={doOpname} data-testid="save-opname-btn">Simpan Opname</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* stock card */}
      <Dialog open={!!card} onOpenChange={(o) => !o && setCard(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader><DialogTitle>Kartu Stok — {card?.name}</DialogTitle></DialogHeader>
          <Table>
            <TableHeader><TableRow><TableHead>Waktu</TableHead><TableHead>Tipe</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>Catatan</TableHead></TableRow></TableHeader>
            <TableBody>
              {cardRows.map((m) => (
                <TableRow key={m.id}>
                  <TableCell className="text-xs">{dateTimeFmt(m.created_at)}</TableCell>
                  <TableCell>{typeLabel[m.type] || m.type}</TableCell>
                  <TableCell className={`text-right font-mono ${m.qty_base < 0 ? "text-rose-600" : "text-emerald-600"}`}>{m.qty_base > 0 ? "+" : ""}{numberFmt(m.qty_base)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{m.note}</TableCell>
                </TableRow>
              ))}
              {!cardRows.length && <TableRow><TableCell colSpan={4} className="py-8 text-center text-muted-foreground">Belum ada pergerakan</TableCell></TableRow>}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </div>
  );
}
